import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Car, Plus, Trash2, Copy, X, Upload, ImagePlus, Camera, ChevronLeft, ChevronRight, Pencil, GripVertical, Shield, PowerOff, Power } from "lucide-react";
import { api } from "../../lib/api";
import { F, Skeleton, NvInputStyles, useConfirm } from "./_shared";
import VehicleInsuranceCardModal from "./VehicleInsuranceCardModal";

/* ---------------- Fleet ---------------- */
export default function FleetTab() {
  const [vehicles, setVehicles] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [photoMgr, setPhotoMgr] = useState(null);
  const [insuranceMgr, setInsuranceMgr] = useState(null);
  const [dragId, setDragId] = useState(null);       // vehicle currently being dragged
  const [dragOverId, setDragOverId] = useState(null); // vehicle currently hovered
  const { confirm, dialog: confirmDialog } = useConfirm();
  const load = useCallback(() => {
    api.get("/admin/vehicles").then((r) => setVehicles(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const askDeleteVehicle = async (v) => {
    const ok = await confirm({
      title: "Delete vehicle?",
      message: "It will be removed from the public fleet and all booking pickers.",
      meta: v.name,
      note: "Existing bookings referencing this vehicle keep their historical name.",
      confirmLabel: "Delete vehicle",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      toast.success("Vehicle deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const update = async (v, patch) => {
    try {
      await api.patch(`/admin/vehicles/${v.id}`, patch);
      setVehicles((prev) => prev.map((x) => x.id === v.id ? { ...x, ...patch } : x));
      toast.success("Updated");
    } catch (e) { toast.error("Failed"); }
  };

  const move = async (v, direction) => {
    // Optimistic swap so the card visibly slides without waiting for network
    setVehicles((prev) => {
      const i = prev.findIndex((x) => x.id === v.id);
      const j = direction === "up" ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    try {
      await api.post(`/admin/vehicles/${v.id}/move`, { direction });
    } catch (e) {
      toast.error("Reorder failed");
      load(); // revert
    }
  };

  // ---- Drag and Drop ----
  const handleDragStart = (e, vid) => {
    setDragId(vid);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData to activate drag
    try { e.dataTransfer.setData("text/plain", vid); } catch (_) {}
  };
  const handleDragOver = (e, vid) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (vid !== dragOverId) setDragOverId(vid);
  };
  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null); setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    // Reorder locally (optimistic) — remove source, insert before/after target
    const prev = [...vehicles];
    const src = prev.find((v) => v.id === sourceId);
    const srcIdx = prev.findIndex((v) => v.id === sourceId);
    const tgtIdx = prev.findIndex((v) => v.id === targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    prev.splice(srcIdx, 1);
    // If we removed something before the target, target index shifted left
    const insertIdx = srcIdx < tgtIdx ? tgtIdx : tgtIdx;
    prev.splice(insertIdx, 0, src);
    setVehicles(prev);
    try {
      await api.post("/admin/vehicles/reorder", {
        ordered_ids: prev.map((v) => v.id),
      });
    } catch (err) {
      toast.error("Reorder failed");
      load();
    }
  };

  const fmtWeek = (start, end) => {
    try {
      const s = new Date(start), e = new Date(end);
      const opt = { month: "short", day: "numeric" };
      return `WK · ${s.toLocaleDateString(undefined, opt).toUpperCase()} – ${e.toLocaleDateString(undefined, opt).toUpperCase()}`;
    } catch { return "THIS WEEK"; }
  };
  const strengthPill = (pct) => {
    if (pct >= 70) return { label: "STRONG", cls: "bg-lime-400 text-ink-900 shadow-[0_0_20px_rgba(163,230,53,0.5)]" };
    if (pct >= 40) return { label: "MODERATE", cls: "bg-purple-500 text-white shadow-[0_0_20px_rgba(168,85,247,0.5)]" };
    return { label: "LIGHT", cls: "bg-white/15 text-white/80" };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="label-caps text-lime-400">Fleet · Week in review</div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="bo-new-vehicle"
            onClick={() => setShowCreate(true)}
            className="bg-lime-400 text-ink-900 rounded-full px-4 py-1.5 text-xs font-semibold label-caps hover:brightness-110 inline-flex items-center gap-1.5">
            <Plus size={12} /> New vehicle
          </button>
          <div className="hidden sm:block text-xs text-white/40">Scroll →</div>
        </div>
      </div>
      <div className="text-[11px] text-white/50 mb-3 inline-flex items-center gap-1.5" data-testid="bo-fleet-dnd-hint">
        <GripVertical size={12} className="text-lime-400" />
        Drag any card to reorder your fleet — or use the ↔ arrows on each card.
      </div>
      {showCreate && (
        <NewVehicleModal
          existingVehicles={vehicles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {editingVehicle && (
        <NewVehicleModal
          editingVehicle={editingVehicle}
          existingVehicles={vehicles}
          onClose={() => setEditingVehicle(null)}
          onCreated={() => { setEditingVehicle(null); load(); }}
        />
      )}
      {duplicateSource && (
        <NewVehicleModal
          prefillFrom={duplicateSource}
          existingVehicles={vehicles}
          onClose={() => setDuplicateSource(null)}
          onCreated={() => { setDuplicateSource(null); load(); }}
        />
      )}
      {photoMgr && (
        <PhotoManagerModal
          vehicle={photoMgr}
          onClose={() => setPhotoMgr(null)}
          onSaved={() => { setPhotoMgr(null); load(); }}
        />
      )}
      {insuranceMgr && (
        <VehicleInsuranceCardModal
          vehicle={insuranceMgr}
          onClose={() => setInsuranceMgr(null)}
          onSaved={() => { setInsuranceMgr(null); load(); }}
        />
      )}
      <div
        data-testid="bo-fleet-scroll"
        className="flex flex-col sm:flex-row sm:overflow-x-auto sm:snap-x sm:snap-mandatory gap-5 pb-6 sm:-mx-8 sm:px-8"
        style={{ scrollbarColor: "#A3E635 transparent" }}
      >
        {vehicles.map((v, idx) => {
          const days = v.days_on_road ?? 0;
          const pct = v.utilization_pct ?? 0;
          const pill = strengthPill(pct);
          const cats = v.category?.split("-") || [];
          const catLabel = cats.map(c => c.toUpperCase()).join(" · ");
          const daysColor = pct >= 70 ? "text-lime-400" : pct >= 40 ? "text-purple-400" : "text-white";
          const isFirst = idx === 0;
          const isLast = idx === vehicles.length - 1;
          return (
            <div key={v.id} data-testid={`bo-vehicle-${v.id}`}
              draggable
              onDragStart={(e) => handleDragStart(e, v.id)}
              onDragOver={(e) => handleDragOver(e, v.id)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, v.id)}
              className={`group sm:snap-start w-full sm:flex-shrink-0 sm:w-[380px] rounded-3xl overflow-hidden bg-ink-900 border transition-all cursor-grab active:cursor-grabbing
                ${dragId === v.id ? "opacity-40 scale-[0.98]" : ""}
                ${dragOverId === v.id && dragId && dragId !== v.id
                  ? "border-lime-400 ring-2 ring-lime-400/50 scale-[1.02]"
                  : "border-white/8 hover:border-lime-400/40"}`}>
              <div className="relative aspect-[9/10] overflow-hidden bg-ink-700">
                <img src={v.image_url} alt={v.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80"; }} />
                <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-transparent to-black/70" />
                {/* Drag handle indicator — visible on hover */}
                <div className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-white/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                     title="Drag to reorder"
                     data-testid={`bo-vehicle-drag-handle-${v.id}`}>
                  <GripVertical size={14} />
                </div>
                {/* Reorder controls — slide vehicle left/right in the fleet list */}
                <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-10">
                  <button
                    type="button"
                    disabled={isFirst}
                    data-testid={`bo-vehicle-move-up-${v.id}`}
                    onClick={() => move(v, "up")}
                    title="Move earlier in the list"
                    className={`w-8 h-8 rounded-full backdrop-blur-md border flex items-center justify-center transition
                      ${isFirst
                        ? "bg-black/30 border-white/10 text-white/25 cursor-not-allowed"
                        : "bg-black/50 border-lime-400/40 text-lime-300 hover:bg-lime-400 hover:text-ink-900"}`}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    disabled={isLast}
                    data-testid={`bo-vehicle-move-down-${v.id}`}
                    onClick={() => move(v, "down")}
                    title="Move later in the list"
                    className={`w-8 h-8 rounded-full backdrop-blur-md border flex items-center justify-center transition
                      ${isLast
                        ? "bg-black/30 border-white/10 text-white/25 cursor-not-allowed"
                        : "bg-black/50 border-lime-400/40 text-lime-300 hover:bg-lime-400 hover:text-ink-900"}`}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
                <div className="absolute inset-x-0 top-8 flex items-end justify-between px-6 gap-3">
                  <div className="font-serif tracking-tight leading-none">
                    <span className={`${daysColor} text-6xl sm:text-7xl font-bold drop-shadow-[0_0_18px_rgba(163,230,53,0.5)]`} data-testid={`bo-vehicle-days-${v.id}`}>{days}</span>
                    <span className="text-white text-4xl sm:text-5xl font-bold"> DAYS</span>
                  </div>
                </div>
                <div className="absolute top-3 left-6 right-6 flex items-center justify-between">
                  <span className="label-caps text-lime-400 text-[10px]">{catLabel || v.category}</span>
                  <span className="label-caps text-[10px] px-3 py-1 rounded-full bg-purple-500/20 text-purple-200 border border-purple-500/40">
                    {fmtWeek(v.week_start, v.week_end)}
                  </span>
                </div>
              </div>
              <div className="p-5 bg-ink-900 space-y-4">
                <div className="h-px w-24 bg-gradient-to-r from-lime-400 to-purple-500" />
                <h3 className="font-serif text-2xl leading-tight">{v.name}</h3>

                {/* Inventory stepper — big, obvious */}
                <div className="rounded-2xl border border-lime-400/40 bg-lime-400/5 p-4 flex items-center justify-between">
                  <div>
                    <div className="label-caps text-lime-400 text-[10px] mb-0.5">FLEET QUANTITY</div>
                    <div className="text-xs text-white/60">How many of this vehicle you own</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button"
                      data-testid={`bo-qty-dec-${v.id}`}
                      onClick={() => update(v, { quantity: Math.max(1, (v.quantity || 1) - 1) })}
                      className="w-9 h-9 rounded-full bg-ink-800 border border-white/10 hover:border-lime-400 text-lime-400 font-bold text-lg">–</button>
                    <input type="number" min={1}
                      key={v.quantity}
                      data-testid={`bo-vehicle-qty-${v.id}`}
                      defaultValue={v.quantity || 1}
                      onBlur={(e) => { const n = Math.max(1, Number(e.target.value)); if (n !== (v.quantity || 1)) update(v, { quantity: n }); }}
                      className="w-14 h-9 text-center font-serif text-2xl bg-ink-800 border border-lime-400/40 rounded-lg focus:border-lime-400 outline-none" />
                    <button type="button"
                      data-testid={`bo-qty-inc-${v.id}`}
                      onClick={() => update(v, { quantity: (v.quantity || 1) + 1 })}
                      className="w-9 h-9 rounded-full bg-lime-400 hover:bg-lime-300 text-ink-900 font-bold text-lg">+</button>
                  </div>
                </div>

                {/* Out-of-service toggle — flips vehicle.out_of_service so
                    the public calendar / rental flow blocks any date until
                    reversed. Uses the existing PATCH endpoint. */}
                <div className={`rounded-2xl border p-4 flex items-center justify-between gap-3 ${
                  v.out_of_service
                    ? "border-red-500/50 bg-red-500/[0.08]"
                    : "border-white/10 bg-white/[0.02]"
                }`}>
                  <div className="min-w-0">
                    <div className={`label-caps text-[10px] mb-0.5 ${v.out_of_service ? "text-red-300" : "text-white/50"}`}>
                      {v.out_of_service ? "Out of service" : "Service status"}
                    </div>
                    <div className="text-xs text-white/60">
                      {v.out_of_service
                        ? (v.out_of_service_reason || "Blocked on all customer calendars")
                        : "Available on customer calendars"}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`bo-vehicle-out-of-service-${v.id}`}
                    onClick={async () => {
                      const goingDown = !v.out_of_service;
                      let reason = null;
                      if (goingDown) {
                        reason = window.prompt("Reason (shown on the admin card)", "Maintenance");
                        if (reason === null) return;
                      }
                      await update(v, {
                        out_of_service: goingDown,
                        out_of_service_reason: goingDown ? (reason || "Out of service") : null,
                      });
                      toast.success(goingDown
                        ? `${v.name} taken out of service`
                        : `${v.name} back in service`);
                    }}
                    className={`px-3 py-2 rounded-full text-[11px] label-caps inline-flex items-center gap-1.5 border transition ${
                      v.out_of_service
                        ? "border-lime-400/50 text-lime-300 hover:bg-lime-400/10"
                        : "border-red-500/50 text-red-300 hover:bg-red-500/10"
                    }`}
                  >
                    {v.out_of_service ? <Power size={12} /> : <PowerOff size={12} />}
                    {v.out_of_service ? "Return to service" : "Take out of service"}
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div>
                    <div className="label-caps text-white/50 text-[10px] mb-1">DAYS ON ROAD</div>
                    <div className="flex items-baseline gap-1">
                      <span className={`font-serif text-4xl ${daysColor}`} data-testid={`bo-vehicle-onroad-${v.id}`}>{days}</span>
                      <span className="font-serif text-lg text-white/40">/7</span>
                    </div>
                  </div>
                  <div className="text-right ml-auto">
                    <div className="label-caps text-white/50 text-[10px] mb-1">INVENTORY</div>
                    <div className="font-serif text-xl text-white">
                      <span data-testid={`bo-vehicle-qty-display-${v.id}`}>{v.quantity || 1}</span>
                      <span className="text-white/40 text-sm"> unit{(v.quantity || 1) > 1 ? "s" : ""}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span data-testid={`bo-vehicle-strength-${v.id}`}
                    className={`label-caps px-4 py-1.5 rounded-full text-xs font-semibold ${pill.cls}`}>
                    {pill.label} · {pct}%
                  </span>
                </div>
                <div className="flex gap-1.5" data-testid={`bo-vehicle-progress-${v.id}`}>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-full ${
                      i < days
                        ? (pct >= 70 ? "bg-lime-400" : pct >= 40 ? "bg-purple-500" : "bg-white/60")
                        : "bg-white/8"
                    }`} />
                  ))}
                </div>
                <div className="text-[10px] text-white/40 label-caps">Fleet utilization · Week in review</div>
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5 text-xs">
                  {(v.available_for === "chauffeur" || v.available_for === "both") ? (
                    <label>
                      <div className="label-caps text-white/50 text-[10px] mb-1">$/hr</div>
                      <input type="number" defaultValue={v.hourly_rate}
                        onBlur={(e) => e.target.value !== String(v.hourly_rate) && update(v, { hourly_rate: Number(e.target.value) })}
                        className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1 focus:border-lime-400 outline-none" />
                    </label>
                  ) : (
                    <label>
                      <div className="label-caps text-white/30 text-[10px] mb-1">$/hr</div>
                      <div className="w-full bg-ink-900/50 border border-white/5 rounded px-2 py-1 text-white/30 italic">n/a</div>
                    </label>
                  )}
                  <label>
                    <div className="label-caps text-white/50 text-[10px] mb-1">$/day</div>
                    <input type="number" defaultValue={v.daily_rate}
                      onBlur={(e) => e.target.value !== String(v.daily_rate) && update(v, { daily_rate: Number(e.target.value) })}
                      className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1 focus:border-lime-400 outline-none" />
                  </label>
                  <label>
                    <div className="label-caps text-white/50 text-[10px] mb-1">Mode</div>
                    <select value={v.available_for}
                      onChange={(e) => update(v, { available_for: e.target.value })}
                      className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1 focus:border-lime-400 outline-none">
                      <option value="chauffeur">Chauf</option>
                      <option value="rental">Rental</option>
                      <option value="both">Both</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
                  <label>
                    <div className="label-caps text-purple-300 text-[10px] mb-1">Deposit ($)</div>
                    <input type="number" min={0}
                      data-testid={`bo-vehicle-deposit-${v.id}`}
                      defaultValue={v.deposit_amount ?? ""}
                      placeholder={`auto ${(v.daily_rate * 0.5).toFixed(0)}`}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === "" ? null : Number(raw);
                        if (n !== (v.deposit_amount ?? null)) update(v, { deposit_amount: n });
                      }}
                      className="w-full bg-ink-800 border border-purple-400/30 rounded px-2 py-1 focus:border-purple-400 outline-none" />
                  </label>
                  <label>
                    <div className="label-caps text-purple-300 text-[10px] mb-1">Mileage / day</div>
                    <input type="number" min={0}
                      data-testid={`bo-vehicle-mileage-${v.id}`}
                      defaultValue={v.daily_mileage_limit ?? ""}
                      placeholder="unlimited"
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === "" ? null : Number(raw);
                        if (n !== (v.daily_mileage_limit ?? null)) update(v, { daily_mileage_limit: n });
                      }}
                      className="w-full bg-ink-800 border border-purple-400/30 rounded px-2 py-1 focus:border-purple-400 outline-none" />
                  </label>
                  {(v.available_for === "chauffeur" || v.available_for === "both") ? (
                    <label>
                      <div className="label-caps text-purple-300 text-[10px] mb-1">Min hrs (chauf)</div>
                      <input type="number" min={0}
                        data-testid={`bo-vehicle-minhours-${v.id}`}
                        defaultValue={v.min_hours ?? 3}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          if (n !== (v.min_hours ?? 3)) update(v, { min_hours: n });
                        }}
                        className="w-full bg-ink-800 border border-purple-400/30 rounded px-2 py-1 focus:border-purple-400 outline-none" />
                    </label>
                  ) : (
                    <label>
                      <div className="label-caps text-white/30 text-[10px] mb-1">Min hrs (chauf)</div>
                      <div className="w-full bg-ink-900/50 border border-white/5 rounded px-2 py-1 text-white/30 italic"
                        data-testid={`bo-vehicle-minhours-${v.id}`}>n/a</div>
                    </label>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
                  <label>
                    <div className="label-caps text-electric text-[10px] mb-1">Plate</div>
                    <input
                      data-testid={`bo-vehicle-plate-${v.id}`}
                      defaultValue={v.license_plate ?? ""}
                      placeholder="ABC-1234"
                      maxLength={12}
                      onBlur={(e) => {
                        const val = e.target.value.trim().toUpperCase();
                        if (val !== (v.license_plate ?? "")) update(v, { license_plate: val || null });
                      }}
                      className="w-full bg-ink-800 border border-electric/30 rounded px-2 py-1 focus:border-electric outline-none uppercase" />
                  </label>
                  <label>
                    <div className="label-caps text-electric text-[10px] mb-1">VIN</div>
                    <input
                      data-testid={`bo-vehicle-vin-${v.id}`}
                      defaultValue={v.vin ?? ""}
                      placeholder="17-char"
                      maxLength={17}
                      onBlur={(e) => {
                        const val = e.target.value.trim().toUpperCase();
                        if (val !== (v.vin ?? "")) update(v, { vin: val || null });
                      }}
                      className="w-full bg-ink-800 border border-electric/30 rounded px-2 py-1 focus:border-electric outline-none uppercase" />
                  </label>
                  <label>
                    <div className="label-caps text-electric text-[10px] mb-1">Color</div>
                    <input
                      data-testid={`bo-vehicle-color-${v.id}`}
                      defaultValue={v.color ?? ""}
                      placeholder="Obsidian"
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (v.color ?? "")) update(v, { color: val || null });
                      }}
                      className="w-full bg-ink-800 border border-electric/30 rounded px-2 py-1 focus:border-electric outline-none" />
                  </label>
                </div>
                <div className="pt-3 flex justify-between items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-3 text-[10px] label-caps">
                    <button
                      data-testid={`bo-vehicle-photos-${v.id}`}
                      onClick={() => setPhotoMgr(v)}
                      className="text-electric hover:text-white inline-flex items-center gap-1">
                      <Upload size={10} /> Manage photos ({v.images?.length || 1})
                    </button>
                    <button
                      data-testid={`bo-vehicle-insurance-${v.id}`}
                      onClick={() => setInsuranceMgr(v)}
                      className={`inline-flex items-center gap-1 ${
                        v.insurance_card_file_id
                          ? "text-lime-300 hover:text-lime-200"
                          : "text-amber-300 hover:text-amber-200"
                      }`}>
                      <Shield size={10} /> {v.insurance_card_file_id ? "Insurance on file" : "Add insurance"}
                    </button>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer text-purple-300 hover:text-purple-200">
                      <input type="checkbox"
                        data-testid={`bo-vehicle-taxexempt-${v.id}`}
                        checked={!!v.tax_exempt}
                        onChange={(e) => update(v, { tax_exempt: e.target.checked })}
                        className="accent-purple-400 w-3 h-3" />
                      {v.tax_exempt ? "No tax" : "Tax on"}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`bo-vehicle-edit-${v.id}`}
                      onClick={() => setEditingVehicle(v)}
                      className="text-[10px] label-caps text-electric hover:text-white inline-flex items-center gap-1">
                      <Pencil size={10} /> Edit
                    </button>
                    <button
                      data-testid={`bo-vehicle-duplicate-${v.id}`}
                      onClick={() => setDuplicateSource(v)}
                      className="text-[10px] label-caps text-lime-400/80 hover:text-lime-300 inline-flex items-center gap-1">
                      <Copy size={10} /> Duplicate
                    </button>
                    <button
                      data-testid={`bo-vehicle-delete-${v.id}`}
                      onClick={() => askDeleteVehicle(v)}
                      className="text-[10px] label-caps text-red-400/70 hover:text-red-300 inline-flex items-center gap-1">
                      <Trash2 size={10} /> Delete vehicle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 justify-center mt-2 text-[10px] text-white/40 label-caps">
        {vehicles.length} vehicles · swipe or scroll to browse
      </div>
      {confirmDialog}
    </div>
  );
}


/* ---------------- Photo Manager (existing vehicle) ---------------- */
function PhotoManagerModal({ vehicle, onClose, onSaved }) {
  const initial = vehicle.images?.length ? vehicle.images : (vehicle.image_url ? [vehicle.image_url] : []);
  const [images, setImages] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/vehicles/upload-image", fd);
        const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setImages((prev) => [...prev, absolute]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
  };

  const removeAt = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const makeCover = (i) => setImages((prev) => {
    if (i === 0) return prev;
    const next = [...prev];
    const [pick] = next.splice(i, 1);
    next.unshift(pick);
    return next;
  });

  const save = async () => {
    if (images.length === 0) return toast.error("At least one photo is required");
    setBusy(true);
    try {
      await api.patch(`/admin/vehicles/${vehicle.id}`, {
        images,
        image_url: images[0],
      });
      toast.success("Photos updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bo-photo-mgr-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-4 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-ink-900 border border-electric/40 rounded-3xl p-6 my-8 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps text-electric text-xs">Manage photos</div>
            <div className="font-serif text-lg">{vehicle.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-white/60">{images.length} photo(s) — first is the cover</div>
          <input ref={fileRef} type="file" multiple accept="image/*"
            data-testid={`bo-photo-mgr-input-${vehicle.id}`}
            className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <button type="button"
            data-testid={`bo-photo-mgr-add-${vehicle.id}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-electric text-ink-900 rounded-full px-4 py-1.5 text-xs font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Upload size={12} /> {uploading ? "Uploading…" : "Add photos"}
          </button>
        </div>

        {images.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((url, i) => (
              <div key={url + i} className="relative rounded-xl overflow-hidden bg-ink-800 aspect-[4/3] group">
                <img src={url} alt={`${vehicle.name} ${i+1}`} className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-2 left-2 text-[9px] label-caps bg-lime-400 text-ink-900 rounded px-1.5 py-0.5">Cover</span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink-950/95 to-transparent flex justify-between opacity-0 group-hover:opacity-100 transition">
                  {i !== 0 && (
                    <button type="button" onClick={() => makeCover(i)}
                      className="text-[10px] text-electric hover:text-white label-caps">Make cover</button>
                  )}
                  <button type="button" onClick={() => removeAt(i)}
                    className="text-[10px] text-red-300 hover:text-red-100 label-caps ml-auto">Remove</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/15 p-6 text-center text-sm text-white/50">
            No photos yet — upload some.
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60 hover:border-white/30">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={busy || images.length === 0}
            data-testid={`bo-photo-mgr-save-${vehicle.id}`}
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-electric text-ink-900 font-semibold hover:brightness-110 disabled:opacity-50">
            {busy ? "Saving…" : "Save photos"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---------------- New Vehicle Modal ---------------- */
function NewVehicleModal({ onClose, onCreated, prefillFrom = null, editingVehicle = null, existingVehicles = [] }) {
  const source = editingVehicle || prefillFrom;
  // For duplicates, default insert-after to the source vehicle so the
  // white Lamborghini lands right next to the red one.
  const [afterVehicleId, setAfterVehicleId] = useState(() => (prefillFrom ? prefillFrom.id : ""));
  const [form, setForm] = useState(() => {
    if (source) {
      const isDup = !!prefillFrom && !editingVehicle;
      return {
        name: isDup ? `${source.name || ""} (Copy)`.trim() : (source.name || ""),
        year: source.year ?? 2026,
        category: source.category || "flagship-suv",
        capacity: source.capacity ?? 4,
        luggage_capacity: source.luggage_capacity ?? 5,
        available_for: source.available_for || "chauffeur",
        hourly_rate: source.hourly_rate ?? 250,
        daily_rate: source.daily_rate ?? 1200,
        weekly_rate: source.weekly_rate ?? "",
        monthly_rate: source.monthly_rate ?? "",
        description: source.description || "",
        rental_min_days: source.rental_min_days ?? 2,
        min_hours: source.min_hours ?? 3,
        quantity: source.quantity ?? 1,
        rideshare_ready: !!source.rideshare_ready,
        deposit_amount: source.deposit_amount ?? "",
        daily_mileage_limit: source.daily_mileage_limit ?? "",
        tax_exempt: !!source.tax_exempt,
        // For duplicates we clear unique fields. For edits we keep them.
        license_plate: isDup ? "" : (source.license_plate || ""),
        vin: isDup ? "" : (source.vin || ""),
        color: source.color || "",
        features: Array.isArray(source.features) ? source.features.join(", ") : (source.features || ""),
        special_features: source.special_features || "",
      };
    }
    return {
      name: "", year: 2026, category: "flagship-suv",
      capacity: 4, luggage_capacity: 5,
      available_for: "chauffeur",
      hourly_rate: 250, daily_rate: 1200,
      weekly_rate: "", monthly_rate: "",
      description: "", rental_min_days: 2, min_hours: 3,
      quantity: 1, rideshare_ready: false,
      deposit_amount: "", daily_mileage_limit: "",
      tax_exempt: false,
      license_plate: "", vin: "", color: "",
      features: "",
      special_features: "",
    };
  });
  const [images, setImages] = useState(() => {
    if (source) {
      const arr = Array.isArray(source.images) ? [...source.images] : [];
      if (source.image_url && !arr.includes(source.image_url)) arr.unshift(source.image_url);
      return arr.filter(Boolean);
    }
    return [];
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [dragOver, setDragOver] = useState(false);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/vehicles/upload-image", fd);
        const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setImages((prev) => [...prev, absolute]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Vehicle name is required");
    if (images.length === 0) return toast.error("Upload at least one photo");
    setBusy(true);
    try {
      const isChauf = form.available_for === "chauffeur" || form.available_for === "both";
      const isRent = form.available_for === "rental" || form.available_for === "both";
      const body = {
        ...form,
        year: Number(form.year), capacity: Number(form.capacity),
        luggage_capacity: Number(form.luggage_capacity),
        hourly_rate: isChauf ? Number(form.hourly_rate) : 0,
        daily_rate: isRent ? Number(form.daily_rate) : 0,
        rental_min_days: isRent ? Number(form.rental_min_days) : 1,
        min_hours: isChauf ? Number(form.min_hours) : 0,
        quantity: Number(form.quantity),
        weekly_rate: isRent && form.weekly_rate ? Number(form.weekly_rate) : null,
        monthly_rate: isRent && form.monthly_rate ? Number(form.monthly_rate) : null,
        deposit_amount: isRent && form.deposit_amount ? Number(form.deposit_amount) : null,
        daily_mileage_limit: isRent && form.daily_mileage_limit ? Number(form.daily_mileage_limit) : null,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
        special_features: form.special_features?.trim() || null,
        images,
        image_url: images[0],
      };
      if (!editingVehicle && afterVehicleId) {
        body.after_vehicle_id = afterVehicleId;
      }
      await (editingVehicle
        ? api.patch(`/admin/vehicles/${editingVehicle.id}`, body)
        : api.post("/admin/vehicles", body));
      toast.success(editingVehicle ? "Vehicle updated" : "Vehicle added to the fleet");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editingVehicle ? "Could not update vehicle" : "Could not create vehicle"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bo-new-vehicle-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl bg-ink-900 border border-lime-400/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-lime-400 text-xs">{editingVehicle ? `Edit vehicle · ${editingVehicle.name}` : (prefillFrom ? "Duplicate vehicle · new plate & VIN required" : "Add vehicle to fleet")}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* Position in fleet — only shown when creating (not editing) */}
        {!editingVehicle && existingVehicles.length > 0 && (
          <div data-testid="bo-nv-position-block" className="rounded-2xl border border-purple-500/40 bg-purple-500/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] font-bold flex items-center justify-center">↔</span>
              <div className="label-caps text-purple-200 text-[11px]">Position in fleet</div>
            </div>
            <F label="Insert this new vehicle right after…">
              <select
                data-testid="bo-nv-insert-after"
                value={afterVehicleId}
                onChange={(e) => setAfterVehicleId(e.target.value)}
                className="nv-input"
              >
                <option value="">— Add at the end of the fleet —</option>
                {existingVehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.color ? ` · ${v.color}` : ""}
                  </option>
                ))}
              </select>
            </F>
            <div className="text-[10px] text-white/50 mt-1.5">
              Tip: to keep a matched pair together (e.g. a white Lamborghini next to the red one), pick the vehicle it should sit beside.
            </div>
          </div>
        )}

        {/* STEP 1 — Photos (prominent drag-drop dropzone) */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-lime-400 text-ink-900 text-[11px] font-bold flex items-center justify-center">1</span>
            <div className="label-caps text-lime-400 text-[11px]">Add vehicle photos <span className="text-red-400">*</span></div>
            <span className="ml-auto text-[10px] text-white/40">{images.length} photo{images.length === 1 ? "" : "s"} added</span>
          </div>

          <input
            ref={fileRef} type="file" multiple accept="image/*,.heic,.heif"
            data-testid="bo-nv-image-input"
            className="hidden"
            onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
          />

          <div
            data-testid="bo-nv-dropzone"
            onClick={() => !uploading && fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files);
            }}
            className={`rounded-2xl border-2 border-dashed cursor-pointer transition-all p-6 sm:p-8 text-center
              ${dragOver
                ? "border-lime-400 bg-lime-400/10 scale-[1.01]"
                : images.length === 0
                  ? "border-lime-400/60 bg-lime-400/5 hover:border-lime-400 hover:bg-lime-400/10"
                  : "border-white/15 bg-ink-950/40 hover:border-lime-400/60"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${images.length === 0 ? "bg-lime-400 text-ink-900" : "bg-lime-400/20 text-lime-400"}`}>
                {uploading ? (
                  <div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ImagePlus size={28} strokeWidth={2.2} />
                )}
              </div>
              <div className="font-serif text-lg text-white">
                {uploading ? "Uploading photos…" : images.length === 0 ? "Tap here to add photos" : "Add more photos"}
              </div>
              <div className="text-xs text-white/60 max-w-sm">
                Take photos with your phone camera, upload from your device, or drag &amp; drop.
                Supports JPG, PNG, HEIC. First photo becomes the fleet cover.
              </div>
              <div className="flex flex-wrap justify-center gap-2 mt-1">
                <button type="button"
                  data-testid="bo-nv-upload-photos"
                  onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                  disabled={uploading}
                  className="bg-lime-400 text-ink-900 rounded-full px-5 py-2 text-xs font-semibold hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Upload size={12} /> Choose files
                </button>
                <button type="button"
                  data-testid="bo-nv-camera"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!fileRef.current) return;
                    fileRef.current.setAttribute("capture", "environment");
                    fileRef.current.click();
                    setTimeout(() => fileRef.current?.removeAttribute("capture"), 500);
                  }}
                  disabled={uploading}
                  className="bg-ink-800 border border-lime-400/40 text-lime-400 rounded-full px-5 py-2 text-xs font-semibold hover:bg-ink-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Camera size={12} /> Use camera
                </button>
              </div>
            </div>
          </div>

          {images.length > 0 && (
            <div data-testid="bo-nv-image-grid" className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
              {images.map((url, i) => (
                <div key={url + i} className="relative rounded-lg overflow-hidden bg-ink-800 aspect-[4/3] border border-white/10">
                  <img src={url} alt={`vehicle ${i+1}`} className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-1 left-1 text-[9px] label-caps bg-lime-400 text-ink-900 rounded px-1.5 py-0.5 font-bold">Cover</span>
                  )}
                  <button type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-ink-950/80 hover:bg-red-500/70 text-white/90 flex items-center justify-center">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STEP 2 — Vehicle details */}
        <div className="flex items-center gap-2 pt-2">
          <span className="w-6 h-6 rounded-full bg-white/10 text-white text-[11px] font-bold flex items-center justify-center">2</span>
          <div className="label-caps text-white/70 text-[11px]">Vehicle details</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <F label="Name *"><input required data-testid="bo-nv-name" value={form.name}
            onChange={(e) => set("name", e.target.value)} className="nv-input" placeholder="2026 Cadillac Escalade V" /></F>
          <F label="Year"><input type="number" data-testid="bo-nv-year" value={form.year}
            onChange={(e) => set("year", e.target.value)} className="nv-input" /></F>
          <F label="Category">
            <select data-testid="bo-nv-category" value={form.category}
              onChange={(e) => set("category", e.target.value)} className="nv-input">
              <option value="flagship-suv">Flagship SUV</option>
              <option value="executive-sedan">Executive Sedan</option>
              <option value="luxury-sedan">Luxury Sedan</option>
              <option value="performance-sedan">Performance Sedan</option>
              <option value="premium-suv">Premium SUV</option>
              <option value="performance-suv">Performance SUV</option>
              <option value="supercar">Supercar</option>
              <option value="passenger-van">Passenger Van</option>
              <option value="rideshare-sedan">Rideshare Sedan</option>
              <option value="rideshare-suv">Rideshare SUV</option>
            </select>
          </F>
          <F label="Available for">
            <select data-testid="bo-nv-availablefor" value={form.available_for}
              onChange={(e) => set("available_for", e.target.value)} className="nv-input">
              <option value="chauffeur">Chauffeur only</option>
              <option value="rental">Rental only</option>
              <option value="both">Both</option>
            </select>
          </F>
          <F label="Quantity"><input type="number" min={1} data-testid="bo-nv-quantity" value={form.quantity}
            onChange={(e) => set("quantity", e.target.value)} className="nv-input" /></F>
          <F label="Capacity (passengers)"><input type="number" min={1} data-testid="bo-nv-capacity" value={form.capacity}
            onChange={(e) => set("capacity", e.target.value)} className="nv-input" /></F>
          <F label="Luggage"><input type="number" min={0} data-testid="bo-nv-luggage" value={form.luggage_capacity}
            onChange={(e) => set("luggage_capacity", e.target.value)} className="nv-input" /></F>
          <F label="$/hour"><input type="number" min={0} data-testid="bo-nv-hourly" value={form.hourly_rate}
            onChange={(e) => set("hourly_rate", e.target.value)} className="nv-input" placeholder="e.g. 150" /></F>
          {(form.available_for === "chauffeur" || form.available_for === "both") && (
            <F label="Min hours (chauffeur)"><input type="number" min={0} data-testid="bo-nv-minhours" value={form.min_hours}
              onChange={(e) => set("min_hours", e.target.value)} className="nv-input" placeholder="3" /></F>
          )}
          {(form.available_for === "rental" || form.available_for === "both") && (
            <>
              <F label="$/day"><input type="number" min={0} data-testid="bo-nv-daily" value={form.daily_rate}
                onChange={(e) => set("daily_rate", e.target.value)} className="nv-input" /></F>
              <F label="$/week (optional)"><input type="number" min={0} data-testid="bo-nv-weekly" value={form.weekly_rate}
                onChange={(e) => set("weekly_rate", e.target.value)} className="nv-input" /></F>
              <F label="$/month (optional)"><input type="number" min={0} data-testid="bo-nv-monthly" value={form.monthly_rate}
                onChange={(e) => set("monthly_rate", e.target.value)} className="nv-input" /></F>
              <F label="Min days (rental)"><input type="number" min={1} data-testid="bo-nv-mindays" value={form.rental_min_days}
                onChange={(e) => set("rental_min_days", e.target.value)} className="nv-input" /></F>
              <F label="Deposit ($) — optional"><input type="number" min={0} data-testid="bo-nv-deposit" value={form.deposit_amount}
                onChange={(e) => set("deposit_amount", e.target.value)} placeholder="auto 50% of daily" className="nv-input" /></F>
              <F label="Daily mileage — optional"><input type="number" min={0} data-testid="bo-nv-mileage" value={form.daily_mileage_limit}
                onChange={(e) => set("daily_mileage_limit", e.target.value)} placeholder="unlimited" className="nv-input" /></F>
            </>
          )}
          <F label="License plate"><input data-testid="bo-nv-plate" value={form.license_plate}
            onChange={(e) => set("license_plate", e.target.value.toUpperCase())} placeholder="ABC-1234" className="nv-input" /></F>
          <F label="VIN"><input data-testid="bo-nv-vin" value={form.vin}
            onChange={(e) => set("vin", e.target.value.toUpperCase())} placeholder="1FTFW1E88MKD12345" maxLength={17} className="nv-input" /></F>
          <F label="Color"><input data-testid="bo-nv-color" value={form.color}
            onChange={(e) => set("color", e.target.value)} placeholder="Obsidian Black" className="nv-input" /></F>
        </div>

        <F label="Description">
          <textarea data-testid="bo-nv-description" value={form.description} rows={2}
            onChange={(e) => set("description", e.target.value)}
            className="nv-input" placeholder="Short marketing blurb shown on the fleet page." />
        </F>
        <F label="Features (comma separated)">
          <input data-testid="bo-nv-features" value={form.features}
            onChange={(e) => set("features", e.target.value)}
            placeholder="Massage seats, Executive rear, WiFi, Sunroof"
            className="nv-input" />
        </F>
        <F label="Special features (this unit only)">
          <textarea data-testid="bo-nv-special-features" value={form.special_features} rows={2}
            onChange={(e) => set("special_features", e.target.value)}
            className="nv-input"
            placeholder="e.g. TVs installed, custom stealth wrap, extended wheelbase — anything that makes THIS unit different from other units of the same model." />
          <div className="text-[10px] text-white/40 mt-1">
            If two vehicles share a name and this field is empty, we treat them as identical
            (only one entry shows up in booking dropdowns). Fill this in to make it a distinct pick.
          </div>
        </F>

        <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer">
          <input type="checkbox" data-testid="bo-nv-rideshare"
            checked={form.rideshare_ready}
            onChange={(e) => set("rideshare_ready", e.target.checked)}
            className="accent-lime-400" />
          Rideshare & delivery approved (Uber / Lyft / DoorDash)
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer ml-4">
          <input type="checkbox" data-testid="bo-nv-tax-exempt"
            checked={!!form.tax_exempt}
            onChange={(e) => set("tax_exempt", e.target.checked)}
            className="accent-purple-400" />
          <span>Tax-exempt <span className="text-white/40">(don&apos;t charge 8.875% on this vehicle)</span></span>
        </label>

        <div className="pt-2 space-y-2">
          {images.length === 0 && (
            <div data-testid="bo-nv-missing-photos" className="flex items-center gap-2 text-[11px] text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1.5">
              <ImagePlus size={12} />
              <span>Add at least one photo above before adding this vehicle to the fleet.</span>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60 hover:border-white/30">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              data-testid="bo-nv-submit"
              className="flex-1 py-2.5 rounded-full text-xs label-caps bg-lime-400 text-ink-900 font-semibold hover:brightness-110 disabled:opacity-50">
              {busy
                ? (editingVehicle ? "Saving…" : "Creating…")
                : (editingVehicle ? "Save changes" : "Add to fleet")}
            </button>
          </div>
        </div>
      </form>
      <style>{`
        .nv-input {
          background: #0f0f11; border: 1px solid rgba(255,255,255,0.08);
          color: #fff; border-radius: 10px; padding: 10px 12px;
          width: 100%; font-size: 13px;
        }
        .nv-input:focus { outline: none; border-color: #A3E635; }
      `}</style>
    </div>
  );
}

